/**
 * RobotIconSelector Component
 *
 * A hybrid icon selection component that allows users to either:
 * 1. Select from a gallery of preset robot icons (passed via props)
 * 2. Upload a custom icon image
 *
 * Features:
 * - Visual gallery with thumbnails showing available preset icons
 * - File upload with image preview
 * - Image validation (type and size)
 * - Display of current robot icon
 *
 * Usage:
 * The selected icon is handled differently based on mode:
 * - Gallery mode: Returns the preset icon path from props
 * - Upload mode: Returns the File object for upload
 *
 * The actual upload happens after robot creation/update via the
 * robotsService.uploadIcon() API call.
 */

import { useState } from "react";
import { Upload, Radio, Space, Card, Image, message, Spin, Button } from "antd";
import {
  UploadOutlined,
  AppstoreOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { servicesUrlConfig } from "shared/config/setting";

/**
 * RobotIconSelector Component
 * Allows users to either select from preset icons or upload a custom icon
 *
 * @param {Object} props
 * @param {string} props.currentIconPath - Current icon path from robot or robot_type
 * @param {File|null} props.iconFile - Currently selected file for upload
 * @param {Function} props.onIconChange - Callback when icon selection/upload changes
 * @param {string} props.mode - 'gallery' or 'upload'
 * @param {Function} props.onModeChange - Callback when mode changes
 * @param {Array} props.presetIcons - Array of preset icon objects with value, label, and path properties
 * @param {boolean} props.loadingIcons - Whether preset icons are currently loading
 */
export const RobotIconSelector = ({
  currentIconPath,
  iconFile,
  onIconChange,
  mode = "gallery",
  onModeChange,
  presetIcons = [],
  loadingIcons = false,
}) => {
  const [previewUrl, setPreviewUrl] = useState(null);

  // Determine which preset icon is currently selected
  const getCurrentPresetValue = () => {
    if (!currentIconPath || presetIcons.length === 0) {
      return presetIcons[0]?.value;
    }

    const preset = presetIcons.find((icon) =>
      currentIconPath.includes(icon.path),
    );
    return preset ? preset.value : presetIcons[0]?.value;
  };

  const handlePresetChange = (e) => {
    const selectedValue = e.target.value;
    const selectedIcon = presetIcons.find(
      (icon) => icon.value === selectedValue,
    );

    if (selectedIcon) {
      onIconChange({
        type: "preset",
        path: selectedIcon.path,
        file: null,
      });
    }
  };

  const beforeUpload = (file) => {
    const isImage = file.type.startsWith("image/");
    if (!isImage) {
      message.error("You can only upload image files!");
      return false;
    }

    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error("Image must be smaller than 5MB!");
      return false;
    }

    // Create preview URL
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target.result);
    };
    reader.readAsDataURL(file);

    // Pass file to parent
    onIconChange({
      type: "upload",
      file: file,
      path: null,
    });

    return false; // Prevent auto upload
  };

  const handleRemoveFile = () => {
    setPreviewUrl(null);
    onIconChange({
      type: "preset",
      path: presetIcons[0]?.path || "robot_type_icons/unknown_robot_type.png",
      file: null,
    });
    onModeChange("gallery");
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <Space direction="vertical" style={{ width: "100%" }}>
        {/* Mode Selection */}
        <Radio.Group
          value={mode}
          onChange={(e) => onModeChange(e.target.value)}
          buttonStyle="solid"
        >
          <Radio.Button value="gallery">
            <AppstoreOutlined /> Icon Gallery
          </Radio.Button>
          <Radio.Button value="upload">
            <UploadOutlined /> Upload Custom
          </Radio.Button>
        </Radio.Group>

        {/* Gallery Mode */}
        {mode === "gallery" && (
          <Card size="small" title="Select an Icon">
            {loadingIcons ? (
              <div style={{ textAlign: "center", padding: "20px" }}>
                <Spin tip="Loading preset icons..." />
              </div>
            ) : (
              <Space direction="vertical" style={{ width: "100%" }}>
                <Radio.Group
                  value={getCurrentPresetValue()}
                  onChange={handlePresetChange}
                  style={{ width: "100%" }}
                >
                  <Space wrap size={[8, 8]}>
                    {presetIcons.map((icon) => (
                      <Radio.Button
                        key={icon.value}
                        value={icon.value}
                        style={{
                          height: 80,
                          width: 80,
                          padding: 4,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <img
                          src={servicesUrlConfig.ROBOTS_API.getStaticUrl(
                            icon.path,
                          )}
                          alt={icon.label}
                          style={{
                            width: 50,
                            height: 50,
                            objectFit: "contain",
                            marginBottom: 4,
                          }}
                        />
                        <div style={{ fontSize: 10 }}>{icon.label}</div>
                      </Radio.Button>
                    ))}
                  </Space>
                </Radio.Group>
              </Space>
            )}
          </Card>
        )}

        {/* Upload Mode */}
        {mode === "upload" && (
          <Card size="small" title="Upload Custom Icon">
            <Space direction="vertical" style={{ width: "100%" }}>
              <Upload
                beforeUpload={beforeUpload}
                onRemove={handleRemoveFile}
                maxCount={1}
                accept="image/*"
                listType="picture"
                fileList={
                  iconFile
                    ? [
                        {
                          uid: "-1",
                          name: iconFile.name,
                          status: "done",
                          url: previewUrl,
                        },
                      ]
                    : []
                }
              >
                <Button icon={<UploadOutlined />}>Select Image</Button>
              </Upload>

              <p style={{ color: "#888", fontSize: 12, margin: 0 }}>
                Supported formats: PNG, JPG, JPEG, WebP
                <br />
                Maximum file size: 5MB
                <br />
                Recommended size: 512x512px
              </p>
            </Space>
          </Card>
        )}

        {/* Unified Preview Container */}
        <Card size="small" title="Preview">
          <Image
            src={
              mode === "upload" && previewUrl
                ? previewUrl
                : mode === "gallery" && getCurrentPresetValue()
                  ? servicesUrlConfig.ROBOTS_API.getStaticUrl(
                      presetIcons.find(
                        (icon) => icon.value === getCurrentPresetValue(),
                      )?.path || currentIconPath,
                    )
                  : currentIconPath
                    ? servicesUrlConfig.ROBOTS_API.getStaticUrl(currentIconPath)
                    : servicesUrlConfig.ROBOTS_API.getStaticUrl(
                        "robot_type_icons/unknown_robot_type.png",
                      )
            }
            alt="Icon preview"
            style={{ maxWidth: 200, maxHeight: 200 }}
            fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg=="
          />
        </Card>
      </Space>
    </div>
  );
};
